import { SetStateAction, useEffect, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useIncubators } from '@/services/lookups/hooks';
import { combineDateTime } from '@/utils/formatting';

import { useUpdateFertilization } from '../../services/stripping/hooks';
import { validation, type Values } from './types';
import { useStrippingContext } from './useStrippingContext';
import {
  calculateIvc,
  getStrippingDonorDisplay,
  getUpdateFertilizationInput,
  transformStrippingData,
} from './utils';

import './StrippingForm.css';

const TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

type Props = {
  visible: boolean;
  setVisible: React.Dispatch<SetStateAction<boolean>>;
};

export const StrippingForm = ({ visible, setVisible }: Props) => {
  const { selectedRow, refetchFertilizations, qcStripperTipLotNumber } =
    useStrippingContext();
  const showPooledDonors = useFeatureFlag('Stripping_Pool_Donor_Display');
  const isIvcDateNightFixEnabled = useFeatureFlag('IVC_Date_Not_Correct_Night');
  const [params] = useSearchParams();
  const strippingTechnicianId = params.get('tech');
  const fertilizationSiteId = params.get('site');
  const [strippingFertilization, setStrippingFertilization] =
    useState<Values>();

  const incubators = useIncubators(fertilizationSiteId);

  const {
    mutate: updateFertilizationMutate,
    isPending: updateFertilizationLoading,
  } = useUpdateFertilization();

  const [lockIvfNum, setLockIvfNum] = useState(true);
  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<Values>({
    resolver: zodResolver(validation),
    defaultValues: {
      ...strippingFertilization,
    },
  });

  const [ivfNumber, ivcBroken, ivcMissing, ivcImmature, ivcExtra, ivcDead] =
    watch([
      'ivfNumber',
      'ivcBroken',
      'ivcMissing',
      'ivcImmature',
      'ivcExtra',
      'ivcDead',
    ]);

  const calcIvc = calculateIvc({
    ivfNumber,
    ivcBroken,
    ivcMissing,
    ivcImmature,
    ivcExtra,
    ivcDead,
  });

  useEffect(() => {
    if (selectedRow) {
      const newData = transformStrippingData(
        selectedRow,
        isIvcDateNightFixEnabled,
      );

      setStrippingFertilization(newData);
      if (newData.qcIncubator === '') {
        reset({ ...newData, qcIncubator: undefined });
      } else {
        reset(newData);
      }
    }
  }, [reset, selectedRow, isIvcDateNightFixEnabled]);

  const onSubmit: SubmitHandler<Values> = async (data: Values) => {
    if (!data) return;
    if (
      data.qcIncubator === '' ||
      data.qcIncubator === null ||
      data.qcIncubator === undefined
    ) {
      return;
    }
    if (!strippingTechnicianId) {
      return;
    }

    const date = data.strippedAtDate ? data.strippedAtDate : new Date();
    const time = data.strippedAtTime ? data.strippedAtTime : new Date();
    const combinedDateTime = combineDateTime(date, time);
    const formattedTime =
      combinedDateTime.getHours().toString().padStart(2, '0') +
      combinedDateTime.getMinutes().toString().padStart(2, '0');

    const input = getUpdateFertilizationInput(
      data,
      combinedDateTime,
      strippingTechnicianId,
      qcStripperTipLotNumber,
      formattedTime,
    );

    if (selectedRow) {
      try {
        await updateFertilizationMutate({
          id: selectedRow.id,
          input,
          timeZone: TIME_ZONE,
        });
        refetchFertilizations();
        setVisible(false);
      } catch (error) {
        console.error('Error updating fertilization:', error);
      }
    }
  };

  const handleKeyDown = (
    event:
      | React.KeyboardEvent<HTMLInputElement>
      | React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (event.key === 'Enter' && isDirty === true) {
      handleSubmit(onSubmit)();
    }
  };

  const handleSave = () => {
    handleSubmit(onSubmit)();
  };

  console.warn({ errors });

  return (
    <Dialog
      header="Add Stripping Details"
      visible={visible}
      onHide={() => {
        if (!visible) return;
        if (isDirty === true) {
          setVisible(false);
        }
      }}
    >
      {selectedRow && (
        <form
          className="stripping-form__form"
          onSubmit={handleSubmit(onSubmit)}
        >
          {Object.keys(errors).length > 0 && (
            <p className="text-tog-red px-4">
              Please fix the errors before saving.
            </p>
          )}

          <div className="stripping-form content-panel">
            <div className="stripping-form__section two">
              <div className="stripping-form__field">
                <label htmlFor="ivfNumber">IVF Number</label>
                <div className="input-with-icon-wrapper">
                  <span className="input-icon-container">
                    <Controller
                      name="ivfNumber"
                      control={control}
                      render={({ field }) => (
                        <InputNumber
                          locale="en-US"
                          value={field.value ?? 0}
                          onValueChange={(e) => field.onChange(e.value)}
                          placeholder="IVF Number"
                          invalid={!!errors.ivfNumber}
                          onKeyDown={handleKeyDown}
                          disabled={lockIvfNum}
                          inputId="ivfNumber"
                        />
                      )}
                    />
                    <Button
                      type="button"
                      icon={lockIvfNum ? 'pi pi-lock' : 'pi pi-lock-open'}
                      onClick={() => setLockIvfNum((prev) => !prev)}
                      className="input-icon-button p-button-text"
                    />
                  </span>
                </div>
              </div>
              <div className="errors">
                {errors.ivfNumber && errors.ivfNumber.message}
              </div>

              <div className="stripping-form__field">
                <label htmlFor="ivcNumber">IVC Number</label>
                <Controller
                  name="ivcNumber"
                  control={control}
                  render={({ field }) => (
                    <InputNumber
                      inputId="ivcNumber"
                      locale="en-US"
                      value={field.value ? field.value : 0}
                      onValueChange={(e) => {
                        field.onChange(e.value);
                      }}
                      invalid={!!errors.ivcNumber}
                      onKeyDown={handleKeyDown}
                      autoFocus
                    />
                  )}
                />
              </div>
              <div className="errors">
                {errors.ivcNumber && errors.ivcNumber.message}
              </div>

              <div className="stripping-form__field">
                <label htmlFor="ivcBroken">Broken</label>
                <Controller
                  name="ivcBroken"
                  control={control}
                  render={({ field }) => (
                    <InputNumber
                      inputId="ivcBroken"
                      locale="en-US"
                      value={field.value ? field.value : 0}
                      onValueChange={(e) => {
                        field.onChange(e.value);
                      }}
                      onKeyDown={handleKeyDown}
                    />
                  )}
                />
              </div>
              <div className="errors">
                {errors.ivcBroken && errors.ivcBroken.message}
              </div>

              <div className="stripping-form__field">
                <label htmlFor="ivcDead">Dead</label>
                <Controller
                  name="ivcDead"
                  control={control}
                  render={({ field }) => (
                    <InputNumber
                      inputId="ivcDead"
                      locale="en-US"
                      value={field.value ? field.value : 0}
                      onValueChange={(e) => {
                        field.onChange(e.value);
                      }}
                      onKeyDown={handleKeyDown}
                    />
                  )}
                />
              </div>
              <div className="errors">
                {errors.ivcDead && errors.ivcDead.message}
              </div>

              <div className="stripping-form__field">
                <label htmlFor="ivcMissing">Missing</label>
                <Controller
                  name="ivcMissing"
                  control={control}
                  render={({ field }) => (
                    <InputNumber
                      inputId="ivcMissing"
                      locale="en-US"
                      value={field.value ? field.value : 0}
                      onValueChange={(e) => {
                        field.onChange(e.value);
                      }}
                      onKeyDown={handleKeyDown}
                    />
                  )}
                />
              </div>
              <div className="errors">
                {errors.ivcMissing && errors.ivcMissing.message}
              </div>

              <div className="stripping-form__field">
                <label htmlFor="ivcImmature">Immature</label>
                <Controller
                  name="ivcImmature"
                  control={control}
                  render={({ field }) => (
                    <InputNumber
                      inputId="ivcImmature"
                      locale="en-US"
                      value={field.value ? field.value : 0}
                      onValueChange={(e) => {
                        field.onChange(e.value);
                      }}
                      onKeyDown={handleKeyDown}
                    />
                  )}
                />
              </div>
              <div className="errors">
                {errors.ivcImmature && errors.ivcImmature.message}
              </div>

              <div className="stripping-form__field">
                <label htmlFor="ivcExtra">Extra</label>
                <Controller
                  name="ivcExtra"
                  control={control}
                  render={({ field }) => (
                    <InputNumber
                      inputId="ivcExtra"
                      locale="en-US"
                      value={field.value ? field.value : 0}
                      onValueChange={(e) => {
                        field.onChange(e.value);
                      }}
                      onKeyDown={handleKeyDown}
                    />
                  )}
                />
              </div>
              <div className="errors">
                {errors.ivcExtra && errors.ivcExtra.message}
              </div>

              <div className="stripping-form__field">
                <label htmlFor="calcIvc">Calc IVC</label>
                <div id="calcIvc" className="non-editable">
                  {calcIvc}
                </div>
              </div>
            </div>

            <div className="stripping-form__section three">
              <div className="stripping-form__field boolean">
                <label htmlFor="hasNoMotileSperm">No Motile Sperm</label>
                <Controller
                  name="hasNoMotileSperm"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="hasNoMotileSperm"
                      {...field}
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.checked);
                      }}
                      onKeyDown={handleKeyDown}
                    />
                  )}
                />
              </div>
              <div className="errors">
                {errors.hasNoMotileSperm && errors.hasNoMotileSperm.message}
              </div>

              <div className="stripping-form__field boolean">
                <label htmlFor="hasFewMotileSperm">Few Motile Sperm</label>
                <Controller
                  name="hasFewMotileSperm"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="hasFewMotileSperm"
                      {...field}
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.checked);
                      }}
                      onKeyDown={handleKeyDown}
                    />
                  )}
                />
              </div>
              <div className="errors">
                {errors.hasFewMotileSperm && errors.hasFewMotileSperm.message}
              </div>

              <div className="stripping-form__field boolean">
                <label htmlFor="isHardToStrip">Hard To Strip</label>
                <Controller
                  name="isHardToStrip"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="isHardToStrip"
                      {...field}
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.checked);
                      }}
                      onKeyDown={handleKeyDown}
                    />
                  )}
                />
              </div>
              <div className="errors">
                {errors.isHardToStrip && errors.isHardToStrip.message}
              </div>

              <div className="stripping-form__field boolean">
                <label htmlFor="isFragile">Fragile</label>
                <Controller
                  name="isFragile"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="isFragile"
                      {...field}
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.checked);
                      }}
                      onKeyDown={handleKeyDown}
                    />
                  )}
                />
              </div>
              <div className="errors">
                {errors.isFragile && errors.isFragile.message}
              </div>

              <div className="stripping-form__field boolean">
                <label htmlFor="hasTwoCellCleavage">2 Cell Cleavage</label>
                <Controller
                  name="hasTwoCellCleavage"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="hasTwoCellCleavage"
                      {...field}
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.checked);
                      }}
                      onKeyDown={handleKeyDown}
                    />
                  )}
                />
              </div>
              <div className="errors">
                {errors.hasTwoCellCleavage && errors.hasTwoCellCleavage.message}
              </div>

              <div className="stripping-form__field boolean">
                <label htmlFor="hasPoorCytoplasmQuality">
                  Poor Cytoplasm Quality
                </label>
                <Controller
                  name="hasPoorCytoplasmQuality"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="hasPoorCytoplasmQuality"
                      {...field}
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.checked);
                      }}
                      onKeyDown={handleKeyDown}
                    />
                  )}
                />
              </div>
              <div className="errors">
                {errors.hasPoorCytoplasmQuality &&
                  errors.hasPoorCytoplasmQuality.message}
              </div>

              <div className="stripping-form__field boolean">
                <label htmlFor="contaminationInFert">
                  Contamination in Fertilization
                </label>
                <Controller
                  name="contaminationInFert"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="contaminationInFert"
                      {...field}
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.checked);
                      }}
                      onKeyDown={handleKeyDown}
                    />
                  )}
                />
              </div>
              <div className="errors">
                {errors.contaminationInFert &&
                  errors.contaminationInFert.message}
              </div>

              <div className="stripping-form__field comment">
                <label htmlFor="ivcComment">Comment</label>
                <Controller
                  name="ivcComment"
                  control={control}
                  render={({ field }) => (
                    <InputTextarea
                      id="ivcComment"
                      {...field}
                      value={field?.value || ''}
                      onKeyDown={handleKeyDown}
                    />
                  )}
                />
              </div>
              <div className="errors">
                {errors.ivcComment && errors.ivcComment.message}
              </div>

              <div className="stripping-form__field">
                <label htmlFor="ivcTechnician">IVC Technician</label>
                <div id="ivcTechnician" className="non-editable">
                  {selectedRow?.ivcTechnician?.name}
                </div>
              </div>
            </div>
            <div className="stripping-form__section one">
              <div className="stripping-form__field">
                <label htmlFor="strippedAtDate">IVC Date</label>
                <Controller
                  name="strippedAtDate"
                  control={control}
                  render={({ field }) => (
                    <Calendar
                      id="strippedAtDate"
                      inputId="strippedAtDate"
                      {...field}
                      value={field.value ? new Date(field.value) : null}
                      placeholder="Need By Date"
                      hourFormat="24"
                      autoFocus={false}
                    />
                  )}
                />
              </div>
              <div className="errors">
                {errors.strippedAtDate && errors.strippedAtDate.message}
              </div>

              <div className="stripping-form__field">
                <label htmlFor="donorSchedule.donor.name">Donor</label>
                <div id="donorSchedule.donor.name" className="non-editable">
                  {getStrippingDonorDisplay(selectedRow, showPooledDonors)}
                </div>
              </div>
              <div className="stripping-form__field">
                <label htmlFor="donorSchedule.sire.name">Sire</label>
                <div id="donorSchedule.sire.name" className="non-editable">
                  {selectedRow?.donorSchedule?.donor
                    ? selectedRow?.donorSchedule?.sire?.name
                    : ''}
                </div>
              </div>
              <div className="stripping-form__field">
                <label htmlFor="donorSchedule.semenTypeValue">Semen Type</label>
                <div id="donorSchedule.semenTypeValue" className="non-editable">
                  {selectedRow?.donorSchedule?.semenTypeValue}
                </div>
              </div>

              <div className="stripping-form__field">
                <label htmlFor="strippedAtTime">IVC Time</label>
                <Controller
                  name="strippedAtTime"
                  control={control}
                  render={({ field }) => (
                    <Calendar
                      id="strippedAtTime"
                      inputId="strippedAtTime"
                      {...field}
                      value={field.value ? new Date(field.value) : null}
                      placeholder="Need By Date"
                      hourFormat="24"
                      timeOnly
                    />
                  )}
                />
              </div>
              <div className="errors">
                {errors.strippedAtTime && errors.strippedAtTime.message}
              </div>

              <div className="stripping-form__field">
                <label htmlFor="qcIncubator">Incubator</label>
                <Controller
                  name="qcIncubator"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      id="qcIncubator"
                      value={field.value}
                      onChange={(e) => field.onChange(e.value)}
                      options={incubators}
                      filter
                      optionLabel="name"
                      optionValue="id"
                      placeholder="Select an incubator"
                      className={errors.qcIncubator ? 'p-invalid' : ''}
                    />
                  )}
                />
              </div>
              <div className="errors">
                {errors.qcIncubator && errors.qcIncubator.message}
              </div>
            </div>
          </div>

          <div className="content-panel buttons">
            <Button
              severity="secondary"
              icon="pi pi-times"
              label="Cancel"
              type="button"
              onClick={() => setVisible(false)}
            />
            <Button
              icon="pi pi-check"
              label="Save"
              type="button"
              loading={updateFertilizationLoading}
              onClick={handleSave}
              disabled={!isDirty}
            />
          </div>
        </form>
      )}
    </Dialog>
  );
};
